import { useState, useEffect, useCallback, useRef } from 'react';
import { Search, Bell, Plus, Menu, X, LogOut, Building2, Shield, Loader2, Package, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useSidebar } from '@/contexts/SidebarContext';
import { useAuth } from '@/hooks/use-auth';
import { useToast } from '@/hooks/use-toast';
import { useLocation } from '@/contexts/LocationContext';
import { usePlatformAdmin } from '@/hooks/use-platform-admin';
import { useNavigate } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { customerService, productService } from '@/lib/packages';

interface SearchResult {
  type: 'customer' | 'product';
  id: string;
  name: string;
  subtitle?: string;
}

export const TopBar = () => {
  const { isCollapsed, toggleSidebar } = useSidebar();
  const { user, signOut, companyId } = useAuth();
  const { currentLocation, availableLocations, setCurrentLocation, isLoading: locationsLoading, canSwitchLocation } = useLocation();
  const { isPlatformAdmin, loading: adminLoading } = usePlatformAdmin();
  const { toast } = useToast();
  const navigate = useNavigate();
  
  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);

  const handleSignOut = async () => {
    try {
      await signOut();
      toast({
        title: "Çıkış Yapıldı",
        description: "Başarıyla çıkış yaptınız.",
      });
      // Navigate to login page after successful sign out
      navigate('/login');
    } catch (error) {
      console.error('Sign out error in TopBar:', error);
      // Even if sign out fails, still show success message since 
      // the improved signOut function handles errors gracefully
      toast({
        title: "Çıkış Yapıldı",
        description: "Oturum sonlandırıldı.",
      });
      // Navigate to login page regardless
      navigate('/login');
    }
  };

  const getUserInitials = () => {
    if (user?.email) {
      return user.email.substring(0, 2).toUpperCase();
    }
    return 'U';
  };

  // Debounced search function
  const performSearch = useCallback(async (term: string) => {
    if (!companyId || term.length < 2) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    try {
      // Search both customers and products in parallel
      const [customers, products] = await Promise.all([
        customerService.searchCustomers(companyId, term, 5),
        productService.searchProducts(companyId, term, 5),
      ]);

      const results: SearchResult[] = [
        ...customers.map(customer => ({
          type: 'customer' as const,
          id: customer.id,
          name: `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || 'Adsız Müşteri',
          subtitle: customer.phone || undefined,
        })),
        ...products.map(product => ({
          type: 'product' as const,
          id: product.id,
          name: product.name,
          subtitle: product.item_code || undefined,
        })),
      ];

      setSearchResults(results);
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, [companyId]);

  // Handle search input change
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (searchTerm.length >= 2) {
      setShowResults(true);
      searchTimeoutRef.current = setTimeout(() => {
        performSearch(searchTerm);
      }, 300);
    } else {
      setSearchResults([]);
      setShowResults(false);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm, performSearch]);

  // Handle click outside to close results
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle result click
  const handleResultClick = (result: SearchResult) => {
    if (result.type === 'customer') {
      navigate(`/customers/${result.id}`);
    } else {
      navigate(`/products/${result.id}`);
    }
    setSearchTerm('');
    setSearchResults([]);
    setShowResults(false);
  };

  return (
    <header className="sticky top-0 z-40 glass-panel-strong border-b border-white/20 p-4">
      <div className="flex items-center justify-between gap-4">
        {/* Sidebar Toggle + Location */}
        <div className="flex items-center gap-2">
          {/* Sidebar Toggle - Only visible on medium screens and up */}
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleSidebar}
            className="hidden md:flex p-2 hover:bg-white/10 mr-2"
          >
            {isCollapsed ? <Menu className="h-5 w-5" /> : <X className="h-5 w-5" />}
          </Button>
          
          {/* Location Selector */}
          <div className="flex items-center gap-2 min-w-0">
            <Building2 className="w-5 h-5 text-accent flex-shrink-0" />
            {locationsLoading ? (
              <span className="font-medium text-sm">Yükleniyor...</span>
            ) : canSwitchLocation && availableLocations.length > 1 ? (
              <Select
                value={currentLocation?.id || ''}
                onValueChange={(value) => {
                  const selectedLocation = availableLocations.find(loc => loc.id === value);
                  if (selectedLocation) {
                    setCurrentLocation(selectedLocation);
                  }
                }}
              >
                <SelectTrigger className="h-8 border-none bg-transparent shadow-none p-0 focus:ring-0 min-w-0">
                  <SelectValue className="font-medium text-sm truncate">
                    {currentLocation?.name || 'Lokasyon Seç'}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {availableLocations.map((location) => (
                    <SelectItem key={location.id} value={location.id}>
                      <div className="flex items-center gap-2">
                        <span>{location.name}</span>
                        {location.is_default && (
                          <span className="text-xs bg-accent/20 text-accent px-1 rounded">
                            Varsayılan
                          </span>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <span className="font-medium text-sm truncate">
                {currentLocation?.name || 'Lokasyon Yok'}
              </span>
            )}
          </div>
        </div>

        {/* Search */}
        <div ref={searchContainerRef} className="flex-1 max-w-md relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground z-10" />
          <Input
            placeholder="Müşteri, ürün ara..."
            className="pl-10 glass-panel"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onFocus={() => searchTerm.length >= 2 && setShowResults(true)}
          />
          
          {/* Search Results Dropdown */}
          {showResults && (searchResults.length > 0 || isSearching) && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-background border border-border rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto">
              {isSearching ? (
                <div className="p-4 text-center text-sm text-muted-foreground flex items-center justify-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Aranıyor...
                </div>
              ) : searchResults.length > 0 ? (
                <div className="p-2">
                  {searchResults.map((result) => (
                    <button
                      key={`${result.type}-${result.id}`}
                      onClick={() => handleResultClick(result)}
                      className="w-full text-left px-3 py-2 rounded-md hover:bg-accent/50 transition-colors flex items-center gap-3 group"
                    >
                      {result.type === 'customer' ? (
                        <Users className="w-4 h-4 text-muted-foreground group-hover:text-accent" />
                      ) : (
                        <Package className="w-4 h-4 text-muted-foreground group-hover:text-accent" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate">{result.name}</div>
                        {result.subtitle && (
                          <div className="text-xs text-muted-foreground truncate">{result.subtitle}</div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="p-4 text-center text-sm text-muted-foreground">
                  Sonuç bulunamadı
                </div>
              )}
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="flex items-center gap-3">
          {/* Platform Admin Button - Only visible to platform admins */}
          {isPlatformAdmin && !adminLoading && (
            <Button 
              variant="ghost" 
              size="icon" 
              className="relative text-yellow-500 hover:text-yellow-400 hover:bg-yellow-500/10"
              onClick={() => navigate('/admin')}
              title="Platform Admin"
            >
              <Shield className="w-5 h-5" />
            </Button>
          )}
          
          <Button 
            variant="ghost" 
            size="icon" 
            className="relative"
            onClick={() => navigate('/notifications')}
          >
            <Bell className="w-5 h-5" />
            <span className="absolute -top-1 -right-1 w-3 h-3 bg-accent rounded-full text-xs"></span>
          </Button>
          
          {/* Mobile Quick Action Button */}
          <Button 
            className="md:hidden" 
            size="icon"
            onClick={() => navigate('/qr-scan')}
          >
            <Plus className="w-5 h-5" />
          </Button>

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="relative h-8 w-8 rounded-full">
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-accent/20 text-accent text-xs">
                    {getUserInitials()}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56" align="end" forceMount>
              <DropdownMenuLabel className="font-normal">
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium leading-none">Kullanıcı</p>
                  <p className="text-xs leading-none text-muted-foreground">
                    {user?.email}
                  </p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleSignOut}>
                <LogOut className="mr-2 h-4 w-4" />
                <span>Çıkış Yap</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};